import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'

function HtmlEntity() {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [mode, setMode] = useState<'encode' | 'decode'>('encode')

  const encodeHtml = (text: string): string => {
    const entities: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;',
      '`': '&#x60;',
      '=': '&#x3D;'
    }
    return text.replace(/[&<>"'`=/]/g, char => entities[char])
  }

  const decodeHtml = (text: string): string => {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  }

  const handleInputChange = (value: string) => {
    setInput(value)
    if (mode === 'encode') {
      setOutput(encodeHtml(value))
    } else {
      try {
        setOutput(decodeHtml(value))
      } catch {
        setOutput('Invalid HTML entities')
      }
    }
  }

  const handleModeChange = (newMode: 'encode' | 'decode') => {
    setMode(newMode)
    setInput('')
    setOutput('')
  }

  const swapInputOutput = () => {
    const newInput = output
    setInput(newInput)
    if (mode === 'encode') {
      setOutput(encodeHtml(newInput))
    } else {
      try {
        setOutput(decodeHtml(newInput))
      } catch {
        setOutput('Invalid HTML entities')
      }
    }
  }

  const clearAll = () => {
    setInput('')
    setOutput('')
  }

  const commonEntities = [
    { char: '&', entity: '&amp;', name: 'Ampersand' },
    { char: '<', entity: '&lt;', name: 'Less than' },
    { char: '>', entity: '&gt;', name: 'Greater than' },
    { char: '"', entity: '&quot;', name: 'Double quote' },
    { char: "'", entity: '&#39;', name: 'Single quote' },
    { char: ' ', entity: '&nbsp;', name: 'Non-breaking space' },
    { char: '©', entity: '&copy;', name: 'Copyright' },
    { char: '®', entity: '&reg;', name: 'Registered' },
    { char: '™', entity: '&trade;', name: 'Trademark' },
    { char: '€', entity: '&euro;', name: 'Euro' },
    { char: '£', entity: '&pound;', name: 'Pound' },
    { char: '¥', entity: '&yen;', name: 'Yen' },
  ]

  return (
    <Layout showBackButton title={t('toolPages.htmlEntity.title')}>
      <div className="flex flex-col p-4 gap-6">
        {/* Mode Toggle */}
        <div className="flex gap-3">
          <button
            onClick={() => handleModeChange('encode')}
            className={`flex-1 flex items-center justify-center gap-2 h-12 rounded-lg font-semibold text-sm transition-all active:scale-[0.98] ${
              mode === 'encode'
                ? 'bg-primary text-white shadow-md shadow-primary/20'
                : 'bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark text-slate-700 dark:text-slate-200'
            }`}
          >
            <span className="material-symbols-outlined text-[20px]">code</span>
            {t('toolPages.htmlEntity.encode')}
          </button>
          <button
            onClick={() => handleModeChange('decode')}
            className={`flex-1 flex items-center justify-center gap-2 h-12 rounded-lg font-semibold text-sm transition-all active:scale-[0.98] ${
              mode === 'decode'
                ? 'bg-primary text-white shadow-md shadow-primary/20'
                : 'bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark text-slate-700 dark:text-slate-200'
            }`}
          >
            <span className="material-symbols-outlined text-[20px]">code_off</span>
            {t('toolPages.htmlEntity.decode')}
          </button>
        </div>

        {/* Input Section */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-end px-1">
            <label className="section-label">
              {mode === 'encode' ? t('toolPages.htmlEntity.plainText') : t('toolPages.htmlEntity.htmlEntities')}
            </label>
            <button 
              onClick={clearAll}
              className="text-xs font-medium text-slate-500 hover:text-primary transition-colors flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-[16px]">backspace</span>
              {t('common.clear')}
            </button>
          </div>
          <textarea
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            className="textarea"
            placeholder={mode === 'encode' ? t('toolPages.htmlEntity.inputEncodePlaceholder') : t('toolPages.htmlEntity.inputDecodePlaceholder')}
            rows={5}
          />
        </div>

        {/* Swap Button */}
        <button
          onClick={swapInputOutput}
          disabled={!output}
          className="self-center p-2 rounded-full bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 hover:border-primary transition-colors disabled:opacity-50"
        >
          <span className="material-symbols-outlined text-slate-500 dark:text-slate-400">swap_vert</span>
        </button>

        {/* Output Section */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-end px-1">
            <label className="section-label">
              {mode === 'encode' ? t('toolPages.htmlEntity.htmlEntities') : t('toolPages.htmlEntity.plainText')}
            </label>
            <CopyButton text={output} />
          </div>
          <textarea
            value={output}
            readOnly
            className="textarea bg-slate-50 dark:bg-[#0d131a]"
            placeholder={t('toolPages.htmlEntity.outputPlaceholder')}
            rows={5}
          />
        </div>

        {/* Common HTML Entities Reference */}
        <div className="flex flex-col gap-3">
          <h3 className="section-label">{t('toolPages.htmlEntity.commonEntities')}</h3>
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="grid grid-cols-3 gap-px bg-slate-200 dark:bg-slate-700 text-xs font-semibold text-slate-500 dark:text-slate-400">
              <div className="bg-slate-50 dark:bg-surface-dark p-2 text-center">{t('toolPages.htmlEntity.char')}</div>
              <div className="bg-slate-50 dark:bg-surface-dark p-2 text-center">{t('toolPages.htmlEntity.entity')}</div>
              <div className="bg-slate-50 dark:bg-surface-dark p-2 text-center">{t('toolPages.htmlEntity.name')}</div>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {commonEntities.map((item, index) => (
                <div key={index} className="grid grid-cols-3 text-sm">
                  <div className="p-2 text-center font-mono text-primary">{item.char}</div>
                  <div className="p-2 text-center font-mono text-slate-600 dark:text-slate-400">{item.entity}</div>
                  <div className="p-2 text-center text-slate-500 dark:text-slate-400 text-xs">{item.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom spacing */}
        <div className="h-8"></div>
      </div>
    </Layout>
  )
}

export default HtmlEntity
